import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { XCircle } from "lucide-react";
import api from "../../lib/api";
import { submitRedsysForm, type RedsysFormPayload } from "../../lib/redsysForm";
import { PagoReintentoCard } from "../../components/pago/PagoReintentoCard";
import { AceptacionCondicionesPago } from "../../components/legal/AceptacionCondicionesPago";
import { PublicPageFrame } from "../../components/layout/PublicPageFrame";

export function PagoKoPage() {
  const [searchParams] = useSearchParams();
  const seguroIdParam = searchParams.get("seguro");
  const seguroId = seguroIdParam ? parseInt(seguroIdParam, 10) : NaN;
  const puedeReintentar = !Number.isNaN(seguroId) && seguroId > 0;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aceptaCondiciones, setAceptaCondiciones] = useState(false);

  const handleRetry = async () => {
    if (!puedeReintentar) return;
    if (!aceptaCondiciones) {
      setError("Debes aceptar las condiciones de compra para reintentar el pago.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<{ formData: RedsysFormPayload }>(
        `/cliente/pagos/iniciar/${seguroId}`
      );
      if (res.data.formData?.actionUrl) {
        submitRedsysForm(res.data.formData);
        return;
      }
      setError("No se pudo preparar el pago. Inténtalo de nuevo.");
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        window.location.href = `/login?sesion=expirada`;
        return;
      }
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Error al iniciar el pago. Inténtalo de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicPageFrame>
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
        <XCircle className="mx-auto h-16 w-16 text-red-600" aria-hidden />
        {puedeReintentar ? (
          <>
            <PagoReintentoCard
              onRetry={handleRetry}
              loading={loading}
              error={error}
              retryDisabled={!aceptaCondiciones}
            >
              <AceptacionCondicionesPago
                checked={aceptaCondiciones}
                onChange={setAceptaCondiciones}
                id="acepta-condiciones-pago-ko"
                className="mt-4 text-left"
              />
              <Link
                to="/dashboard"
                className="mt-4 inline-block text-sm text-[#1A3AB8] hover:underline"
              >
                Volver al panel
              </Link>
            </PagoReintentoCard>
          </>
        ) : (
          <>
            <h1 className="mt-4 text-xl font-semibold text-[#2C2C2C]">
              El pago no se ha completado
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Ha ocurrido un error. Vuelve al panel e inicia el pago de nuevo desde el seguro.
            </p>
            <Link
              to="/dashboard"
              className="mt-6 inline-block rounded-lg bg-[#1A3AB8] px-4 py-2.5 font-medium text-white hover:bg-[#15308a]"
            >
              Ir al panel
            </Link>
          </>
        )}
      </div>
    </PublicPageFrame>
  );
}
